import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useEffect, useRef, useState } from "react";
import useAuth from "../hooks/useAuth";
import useLogout from "../hooks/useLogout";
import { useShoppingList } from "../context/ShoppingListProvider";

const PRIMARY_LINKS = [
  { to: '/', label: 'Lounge', icon: 'bi-house-door', end: true },
  { to: '/recipes', label: 'Recipes', icon: 'bi-journal-bookmark' },
  { to: '/shopping-list', label: 'List', icon: 'bi-basket', badge: true },
  { to: '/map', label: 'Map', icon: 'bi-geo-alt' },
];

const STORE_LINKS = [
  { to: '/wgmsjames', label: 'Wegmans James St.' },
  { to: '/pcerie', label: 'Price Chopper Erie Blvd.' },
];

const NavItems = ({
  uncheckedCount,
  location,
  openMenu,
  setOpenMenu,
  storesActive,
  variant,
}) => {
  const linkClass = ({ isActive }) =>
    `app-nav-link${isActive ? ' is-active' : ''}`;

  return (
    <>
      {PRIMARY_LINKS.map((link) => (
        <NavLink
          key={`${variant}-${link.to}`}
          to={link.to}
          end={link.end}
          className={linkClass}
        >
          <i className={`bi ${link.icon}`} aria-hidden="true" />
          <span>{link.label}</span>
          {link.badge && uncheckedCount > 0 && (
            <span className="nav-badge">{uncheckedCount}</span>
          )}
        </NavLink>
      ))}

      <div className={`app-menu${openMenu === 'stores' ? ' is-open' : ''}${storesActive ? ' is-active' : ''}`}>
        <button
          type="button"
          className={`app-nav-link app-nav-button${storesActive ? ' is-active' : ''}`}
          aria-expanded={openMenu === 'stores'}
          aria-haspopup="true"
          onClick={() => setOpenMenu((prev) => (prev === 'stores' ? null : 'stores'))}
        >
          <i className="bi bi-shop" aria-hidden="true" />
          <span>Stores</span>
          <i className="bi bi-chevron-down app-nav-caret" aria-hidden="true" />
        </button>
        <div className="app-menu-panel" role="menu">
          {STORE_LINKS.map((item) => (
            <Link
              key={`${variant}-${item.to}`}
              to={item.to}
              role="menuitem"
              className={`app-menu-item${location.pathname === item.to ? ' is-active' : ''}`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>

      <NavLink to="/billing" className={linkClass}>
        <i className="bi bi-credit-card" aria-hidden="true" />
        <span>Plans</span>
      </NavLink>
    </>
  );
};

const Fakebar = () => {
  const { auth } = useAuth();
  const logoutFn = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const { uncheckedCount } = useShoppingList();
  const headerRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark';
  });

  useEffect(() => {
    const theme = isDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [isDarkMode]);

  useEffect(() => {
    setMenuOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (event.target.closest('.app-header') || event.target.closest('.mobile-menu')) {
        return;
      }
      setOpenMenu(null);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setOpenMenu(null);
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const toggleTheme = () => setIsDarkMode((prev) => !prev);

  const handleLogout = async () => {
    setOpenMenu(null);
    setMenuOpen(false);
    await logoutFn();
    navigate('/login');
  };

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setOpenMenu(null);
  };

  const storesActive = STORE_LINKS.some((item) => location.pathname === item.to);
  const displayName = auth?.user || '';
  const initial = displayName.slice(0, 1).toUpperCase();

  return (
    <>
      <header className="app-header" ref={headerRef}>
        <div className="app-header-inner">
          <Link className="app-logo" to="/" aria-label="Recipe Lab home">
            <span className="app-logo-mark" aria-hidden="true">
              <i className="bi bi-egg-fried" />
            </span>
            <span className="app-logo-text">Recipe Lab</span>
          </Link>

          <nav className="app-nav" aria-label="Main">
            <NavItems
              variant="desktop"
              uncheckedCount={uncheckedCount}
              location={location}
              openMenu={openMenu}
              setOpenMenu={setOpenMenu}
              storesActive={storesActive}
            />
          </nav>

          <div className="app-header-actions">
            <button
              type="button"
              className="app-icon-btn"
              onClick={toggleTheme}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <i className={`bi ${isDarkMode ? 'bi-sun-fill' : 'bi-moon-fill'}`} />
            </button>

            {auth?.user ? (
              <div className={`app-menu app-user-menu${openMenu === 'user' ? ' is-open' : ''}`}>
                <button
                  type="button"
                  className="app-user-chip"
                  aria-expanded={openMenu === 'user'}
                  aria-haspopup="true"
                  onClick={() => setOpenMenu((prev) => (prev === 'user' ? null : 'user'))}
                >
                  <span className="app-user-avatar">{initial}</span>
                  <span className="app-user-name">{displayName}</span>
                  <i className="bi bi-chevron-down app-nav-caret" aria-hidden="true" />
                </button>
                <div className="app-menu-panel app-menu-panel-end" role="menu">
                  <Link to="/billing" role="menuitem" className="app-menu-item">
                    <i className="bi bi-credit-card" aria-hidden="true" /> Plans
                  </Link>
                  <Link to="/shopping-list" role="menuitem" className="app-menu-item">
                    <i className="bi bi-basket" aria-hidden="true" /> Shopping list
                  </Link>
                  <button type="button" role="menuitem" className="app-menu-item" onClick={handleLogout}>
                    <i className="bi bi-box-arrow-right" aria-hidden="true" /> Sign out
                  </button>
                </div>
              </div>
            ) : (
              <NavLink to="/login" className="app-signin-btn">
                Sign in
              </NavLink>
            )}

            <button
              type="button"
              className={`app-header-toggle${menuOpen ? ' is-open' : ''}`}
              aria-expanded={menuOpen}
              aria-controls="app-mobile-menu"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => {
                setOpenMenu(null);
                setMenuOpen((prev) => !prev);
              }}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      <button
        type="button"
        className={`mobile-menu-scrim${menuOpen ? ' show' : ''}`}
        aria-label="Close menu"
        tabIndex={menuOpen ? 0 : -1}
        onClick={closeMobileMenu}
      />

      <div
        id="app-mobile-menu"
        className={`mobile-menu${menuOpen ? ' show' : ''}`}
        aria-hidden={!menuOpen}
      >
        <button
          type="button"
          className="mobile-menu-close"
          aria-label="Close menu"
          onClick={closeMobileMenu}
        >
          ×
        </button>
        <nav className="mobile-menu-nav" aria-label="Mobile">
          <NavItems
            variant="mobile"
            uncheckedCount={uncheckedCount}
            location={location}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            storesActive={storesActive}
          />
        </nav>
      </div>
    </>
  );
};

export default Fakebar;
